import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Clock, FileWarning, User, AlertTriangle,
  CheckCircle2, XCircle, PauseCircle, Info, RefreshCw, X, Lock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { QueuePager } from "@/components/admin/QueuePager";

const RISK_COLORS = {
  low: "bg-green-100 text-green-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const STATUS_LABELS = {
  pending_human_approval: "Pending Human Approval",
  info_requested: "Info Requested",
  verified: "Verified",
  rejected: "Rejected",
  restricted: "Restricted",
  suspended: "Suspended",
};

const FILTERS = [
  { key: "", label: "Pending" },
  { key: "unassigned", label: "Unassigned" },
  { key: "assigned_to_me", label: "Assigned to me" },
  { key: "missing_documents", label: "Missing docs" },
  { key: "info_requested", label: "Info requested" },
  { key: "rejected", label: "Rejected" },
  { key: "restricted", label: "Restricted" },
  { key: "suspended", label: "Suspended" },
];

const DECISIONS = [
  { key: "approve_verify", label: "Approve & Verify", icon: CheckCircle2, cls: "bg-[#16A34A] text-white hover:bg-green-700" },
  { key: "request_more_info", label: "Request More Info", icon: Info, cls: "border border-blue-200 text-blue-700 hover:bg-blue-50" },
  { key: "keep_restricted", label: "Keep Restricted", icon: ShieldAlert, cls: "border border-amber-200 text-amber-700 hover:bg-amber-50" },
  { key: "reject", label: "Reject Application", icon: XCircle, cls: "border border-red-200 text-red-600 hover:bg-red-50" },
  { key: "suspend", label: "Suspend Account", icon: PauseCircle, cls: "border border-slate-300 text-slate-700 hover:bg-slate-100" },
];

function decodeErr(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d.error === "mfa_required") return "MFA required: complete multi-factor authentication (log in with your authenticator) before approving.";
    if (d.error === "stale_case") return "This case changed since you opened it. Reloading…";
    if (d.error === "hard_stop_present") return "TradeGuard hard-stop present. Enable Override with a reason to approve.";
    return d.message || JSON.stringify(d);
  }
  if (err?.response?.status === 429) return "Rate limit reached — too many approval actions. Wait a moment.";
  return formatApiError(d);
}

export default function ApprovalQueueTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [risk, setRisk] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState({ total: 0, hasMore: false });

  const load = useCallback((append = false, offset = 0) => {
    setLoading(true);
    const params = { skip: append ? offset : 0, limit: 100 };
    if (risk) params.risk = risk;
    if (["unassigned", "assigned_to_me", "missing_documents"].includes(filter)) params.filter = filter;
    else if (filter) params.status = { restricted: "verification_restricted" }[filter] || filter;
    api.get("/admin/approvals/queue", { params })
      .then(({ data }) => {
        setRows((prev) => (append ? [...prev, ...(data.queue || [])] : data.queue || []));
        setPage({ total: data.total ?? (data.queue || []).length, hasMore: !!data.has_more });
      })
      .catch((e) => toast.error(decodeErr(e)))
      .finally(() => setLoading(false));
  }, [filter, risk]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="approval-queue-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid="approval-advisory-banner">
          <Lock size={15} className="text-blue-600" />
          Final decision requires an authorized human reviewer. AI &amp; TradeGuard are advisory only.
        </div>
        <button onClick={() => load()} data-testid="approval-refresh" className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 flex items-center gap-1.5 hover:bg-slate-50">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key || "pending"} onClick={() => setFilter(f.key)} data-testid={`approval-filter-${f.key || "pending"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === f.key ? "bg-[#0A0F1C] text-white border-[#0A0F1C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {f.label}
          </button>
        ))}
        <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="approval-risk-filter"
          className="ml-auto h-8 px-2 rounded-md border border-slate-200 text-xs text-slate-600">
          <option value="">All risk</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      <QueuePager shown={rows.length} total={page.total} hasMore={page.hasMore} loading={loading}
        onMore={() => load(true, rows.length)} testid="approval-pager" />
      {rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 bg-white rounded-lg p-12 text-center text-slate-400" data-testid="approval-empty">
          No applications match this view.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Company</th>
                <th className="text-left font-semibold px-4 py-2.5">Submitted</th>
                <th className="text-left font-semibold px-4 py-2.5">Age</th>
                <th className="text-left font-semibold px-4 py-2.5">Risk</th>
                <th className="text-left font-semibold px-4 py-2.5">Docs</th>
                <th className="text-left font-semibold px-4 py-2.5">TradeGuard</th>
                <th className="text-left font-semibold px-4 py-2.5">Reviewer</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`approval-row-${r.user_id}`}>
                  <td className="px-4 py-3 font-medium text-[#0A0F1C]">
                    {r.company}
                    {r.compliance_flags?.length > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-red-600" data-testid={`approval-flag-${r.user_id}`}>
                        <AlertTriangle size={11} /> {r.compliance_flags.length} hard-stop
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{(r.submitted_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.age_hours != null ? `${r.age_hours}h` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${RISK_COLORS[r.risk_level] || "bg-slate-100 text-slate-500"}`}>
                      {r.risk_level || "—"} {r.risk_score != null ? `(${r.risk_score})` : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3">{r.missing_documents ? <FileWarning size={15} className="text-amber-500" /> : <CheckCircle2 size={15} className="text-green-500" />}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs capitalize">{r.tradeguard_recommendation?.replace(/_/g, " ") || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.assigned_reviewer ? <User size={13} className="inline" /> : <span className="text-slate-300">Unassigned</span>}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">{STATUS_LABELS[r.status] || r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(r.user_id)} data-testid={`approval-open-${r.user_id}`}
                      className="h-8 px-3 rounded-md bg-[#2563EB] text-white text-xs font-medium hover:bg-blue-700">Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <CaseDrawer userId={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-[#0A0F1C] break-words">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

function CaseDrawer({ userId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState("");
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/approvals/case/${userId}`).then(({ data }) => setData(data)).catch((e) => toast.error(decodeErr(e)));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const tg = data?.tradeguard || {};
  const hardStop = tg.has_hard_stop;

  const submit = async () => {
    if (!decision) return toast.error("Choose a decision.");
    if (!reason.trim()) return toast.error("A decision reason is required.");
    if (decision === "approve_verify" && hardStop && !override) return toast.error("Hard-stop present — enable Override with a reason.");
    setBusy(true);
    try {
      const body = { decision, reason, expected_version: data?.case?.version };
      if (override) { body.override = true; body.override_reason = overrideReason; }
      const { data: res } = await api.post(`/admin/approvals/${userId}/decision`, body);
      toast.success(res.idempotent ? "No change (already applied)." : `Decision recorded: ${STATUS_LABELS[res.new_status] || res.new_status}`);
      onDone();
    } catch (e) {
      const msg = decodeErr(e);
      toast.error(msg);
      if (e?.response?.status === 409) load();
    } finally { setBusy(false); setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="approval-case-drawer" onClick={() => { if (!confirming) onClose(); }}>
      <div className="w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg text-[#0A0F1C]">{data?.legal_business?.legal_name || data?.case?.company || userId}</div>
            <div className="text-xs text-slate-400">Case {data?.case?.case_id} · {STATUS_LABELS[data?.current_status] || data?.current_status}</div>
          </div>
          <button onClick={onClose} data-testid="approval-drawer-close" className="p-1.5 rounded-md hover:bg-slate-100"><X size={18} /></button>
        </div>

        {!data ? (
          <div className="p-10 text-center text-slate-400">Loading case…</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              <Info size={14} /> {data.advisory_notice}
            </div>

            <div className={`rounded-lg border p-4 ${hardStop ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`} data-testid="approval-tradeguard-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-semibold text-[#0A0F1C]">
                  {hardStop ? <ShieldAlert size={16} className="text-red-600" /> : <ShieldCheck size={16} className="text-green-600" />}
                  TradeGuard · {tg.rule_version}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${RISK_COLORS[tg.risk_level] || "bg-slate-100"}`}>
                  {tg.risk_level} · score {tg.risk_score}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Recommendation (advisory): <span className="font-medium capitalize">{tg.recommendation?.replace(/_/g, " ")}</span></div>
              {(tg.findings || []).length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="approval-findings">
                  {tg.findings.map((f, i) => (
                    <li key={i} className="text-xs flex items-start gap-2">
                      <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${f.severity === "hard_stop" ? "bg-red-100 text-red-700" : f.severity === "review" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>{f.severity}</span>
                      <span className="text-slate-600"><b>{f.rule_id}</b> — {f.explanation}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Jurisdiction" value={data.legal_business?.jurisdiction} />
              <Field label="Address" value={data.legal_business?.address} />
              <Field label="Registration #" value={data.registration?.registration_number} />
              <Field label="SOS Status" value={data.registration?.sos_status} />
              <Field label="Authorized Rep" value={typeof data.authorized_representative === "string" ? data.authorized_representative : JSON.stringify(data.authorized_representative)} />
              <Field label="Documents" value={`${data.document_count} uploaded`} />
              <Field label="SAM Excluded" value={String(data.sanctions?.sam_excluded ?? "—")} />
              <Field label="Sanctions" value={data.sanctions?.sanctions_component || "—"} />
              <Field label="AI Recommendation" value={data.ai_recommendation?.recommendation} />
              <Field label="Assigned Reviewer" value={data.assigned_reviewer} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Review history &amp; prior decisions</div>
              <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto text-xs divide-y divide-slate-100" data-testid="approval-history">
                {[...(data.decisions || []), ...(data.review_history || [])].length === 0 ? (
                  <div className="p-3 text-slate-300">No prior activity.</div>
                ) : (
                  <>
                    {(data.decisions || []).map((d, i) => (
                      <div key={`d${i}`} className="p-2.5 flex justify-between"><span className="text-slate-600">{d.decision} → {d.new_status} <i className="text-slate-400">by {d.reviewer_role}</i></span><span className="text-slate-400">{(d.created_at || "").slice(0, 16)}</span></div>
                    ))}
                    {(data.review_history || []).map((h, i) => (
                      <div key={`h${i}`} className="p-2.5 flex justify-between"><span className="text-slate-500">{h.action}</span><span className="text-slate-400">{(h.created_at || "").slice(0, 16)}</span></div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Decision panel */}
            <div className="border-t border-slate-200 pt-5 space-y-3" data-testid="approval-decision-panel">
              <div className="text-sm font-semibold text-[#0A0F1C]">Reviewer Decision</div>
              <div className="flex flex-wrap gap-2">
                {DECISIONS.map((d) => (
                  <button key={d.key} onClick={() => setDecision(d.key)} data-testid={`approval-decision-${d.key}`}
                    className={`h-9 px-3 rounded-md text-sm font-medium flex items-center gap-1.5 ${decision === d.key ? "ring-2 ring-offset-1 ring-[#2563EB] " : ""}${d.cls}`}>
                    <d.icon size={15} /> {d.label}
                  </button>
                ))}
              </div>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="approval-reason-input"
                placeholder="Decision reason / note (required)…" rows={3}
                className="w-full rounded-md border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
              {decision === "approve_verify" && hardStop && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2" data-testid="approval-override-box">
                  <label className="flex items-center gap-2 text-sm text-red-700 font-medium">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="approval-override-check" />
                    Override TradeGuard hard-stop to approve
                  </label>
                  {override && (
                    <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="approval-override-reason"
                      placeholder="Override justification (required)…" className="w-full rounded-md border border-red-200 p-2 text-sm" />
                  )}
                </div>
              )}
              {!confirming ? (
                <button onClick={() => { if (!decision) return toast.error("Choose a decision."); if (!reason.trim()) return toast.error("Reason required."); setConfirming(true); }}
                  data-testid="approval-submit" disabled={busy}
                  className="w-full h-10 rounded-md bg-[#0A0F1C] text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
                  Submit Decision
                </button>
              ) : (
                <div className="rounded-md border border-slate-300 bg-slate-50 p-3 space-y-2" data-testid="approval-confirm-box">
                  <div className="text-sm text-slate-700">
                    Confirm <b>{DECISIONS.find((d) => d.key === decision)?.label}</b>
                    {decision === "approve_verify" && " — this grants full Verified access and requires an MFA-verified session."}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={submit} disabled={busy} data-testid="approval-confirm-yes"
                      className="flex-1 h-9 rounded-md bg-[#16A34A] text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60">
                      {busy ? "Submitting…" : "Confirm"}
                    </button>
                    <button onClick={() => setConfirming(false)} disabled={busy} data-testid="approval-confirm-cancel"
                      className="h-9 px-4 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-white">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
